import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { Icons, Progress, Spinner, useHelmet } from '@agro-club/agroclub-shared'

import { useSingleEntity, useTableData } from 'modules/domain/common/hooks'
import { Company } from 'modules/domain/company/types'
import { User } from 'modules/domain/user/types'
import { endpoints } from 'modules/endpoints'
import styled from 'styled-components'
import APageWrapper from 'views/components/PageWrapper/APageWrapper'
import ItemLoadingLayout from 'views/layouts/ItemLoadingLayout/ItemLoadingLayout'
import * as Layout from 'views/layouts/NewLayout'
import { CompanyBranches } from 'views/pages/Company/CompanyDetail/CompanyBranches/CompanyBranches'
import CompanyRoutes from 'views/pages/Company/routes'
import * as Header from 'views/ui/Header/Header'
import { AnalyticsPages, AnalyticsPlaces } from '../analyticsPlaces'
import { CompanyFactoring } from './CompanyFactoring/CompanyFactoring'
import { CompanyGeneral } from './CompanyGeneral/CompanyGeneral'
import { CompanySubtitle } from './CompanySubtitle'
import { FactoringAllStatuses } from './FactoringAllStatuses'
import { SectionsWrapper, TitleWrapper } from './styled'
import { defaultListRequestParams } from 'views/components/TableFilters/data'
import { useCallback, useState } from 'react'
import { ListRequestParams } from 'modules/domain/types'
import { PAGE_SIZE_1K } from 'modules/constants'
import { AButton } from 'views/components/Analytics'
import { AddBranchModal } from './CompanyBranches/AddBranchModal'

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 802px 802px;
  height: 100%;
  column-gap: 24px;
  overflow-y: auto;
`

export const CompanyDetail = () => {
  const { t } = useTranslation('company')
  const { id } = useParams()
  const [, companyUsers] = useSingleEntity<User[]>(endpoints.employee(id), false)
  const [fetchProgress, company, refreshCompany] = useSingleEntity<Company>(endpoints.company(id))
  const parentId = company?.parent?.id || company?.id
  const [listRequestParams] = useState<ListRequestParams>({ ...defaultListRequestParams, pageSize: PAGE_SIZE_1K })
  const {
    data: branches,
    refetch: refetchBranches,
    progress: branchesProgress,
  } = useTableData<Company>(parentId ? endpoints.companyBranches(parentId) : '', listRequestParams, !!parentId)
  const [showBranchModal, setShowBranchModal] = useState(false)

  const refetch = useCallback(async () => {
    await refreshCompany(true)
    await refetchBranches()
  }, [refreshCompany, refetchBranches])

  useHelmet({ title: company?.name || '' })

  if (fetchProgress === Progress.WORK || !company) {
    return <ItemLoadingLayout id={id} />
  }

  return (
    <APageWrapper page={AnalyticsPages.COMPANY_DETAIL} place={AnalyticsPlaces.DETAIL} id={id}>
      <Layout.WrapperContentFlex>
        <Layout.Header>
          <Layout.TopHeader>
            <Header.Breadcrumbs
              routes={[
                { path: CompanyRoutes.List, breadcrumbName: t('list.heading') },
                { breadcrumbName: company.name },
              ]}
            />
            <AButton
              id="addCompanyBranch"
              color="approve"
              Icon={Icons.IconAdd}
              onClick={() => setShowBranchModal(true)}
            >
              {t('addChildCompany')}
            </AButton>
          </Layout.TopHeader>
          <Layout.PageTitleWrapper>
            <TitleWrapper>
              <Header.Title compact title={company.name} />
              <FactoringAllStatuses company={company} />
              {branchesProgress == Progress.WORK && <Spinner size="small" />}
            </TitleWrapper>
            <CompanySubtitle company={company} />
          </Layout.PageTitleWrapper>
        </Layout.Header>

        <ContentWrapper>
          <SectionsWrapper>
            <CompanyFactoring company={company} refetchCompany={refreshCompany} />
            <CompanyGeneral refreshCompany={refreshCompany} company={company} companyUsers={companyUsers} />
          </SectionsWrapper>

          {branches && branches.length > 1 && (
            <CompanyBranches branches={branches} currentCompanyId={company.id} refetch={refetch} />
          )}
        </ContentWrapper>
      </Layout.WrapperContentFlex>
      {showBranchModal && parentId && (
        <AddBranchModal
          onClose={() => setShowBranchModal(false)}
          parentId={parentId}
          parentName={company.parent?.name || company.name}
          refetch={refetch}
        />
      )}
    </APageWrapper>
  )
}
